import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { auth, db } from "../firebase";
import { brandColors } from "../theme";
import { toggleFollow } from "../utils/follow";
import { Close as CloseIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { T } from "../routes/Translate";
import { DESKTOP_MEDIA_QUERY } from "../pageLayout";
import SearchClearAdornment from "./SearchClearAdornment";

// Mismas colecciones que los contadores del perfil (ProfilePage.js):
// userFollowing/{uid} = a quién sigue, companyFollowers/{uid} = quién lo sigue.
// Las dos son mapas { uid: ... }.
const SOURCES = {
  following: "userFollowing",
  followers: "companyFollowers",
};

const normalize = (value) =>
  String(value || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Botón Seguir con contraste en todos los temas: relleno con el acento y el
// texto que contrasta con él (mismo criterio que StoresAppBar.js).
const followTextColor = brandColors.mode === "light" ? "#ffffff" : "#0a0a1a";

const FollowListDialog = ({ open, onClose, uid, initialTab = "following", onFollowChange }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const [tab, setTab] = useState(initialTab);
  const [lists, setLists] = useState({});
  const [search, setSearch] = useState("");
  const myUid = auth.currentUser?.uid;
  // A quién sigo yo y a quién le mandé solicitud (cuentas privadas), para
  // pintar el botón de cada fila.
  const [myFollowing, setMyFollowing] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || !myUid) return;
    Promise.all([
      getDoc(doc(db, "userFollowing", myUid)),
      getDoc(doc(db, "userFollowRequests", myUid)),
    ])
      .then(([followingSnap, requestsSnap]) => {
        setMyFollowing(followingSnap.exists() ? Object.keys(followingSnap.data()) : []);
        setMyRequests(requestsSnap.exists() ? Object.keys(requestsSnap.data()) : []);
      })
      .catch((error) => console.error("Error cargando a quién seguís:", error));
  }, [open, myUid]);

  const handleFollow = async (user) => {
    if (!myUid || busyId) return;
    setBusyId(user.id);
    try {
      const result = await toggleFollow({
        currentUid: myUid,
        currentDisplayName: auth.currentUser?.displayName,
        targetUid: user.id,
        isFollowing: myFollowing.includes(user.id),
        targetIsPrivate: user.cuentaPrivada === true,
        hasPendingRequest: myRequests.includes(user.id),
      });
      if (result === "followed") setMyFollowing((prev) => [...prev, user.id]);
      else if (result === "unfollowed") setMyFollowing((prev) => prev.filter((id) => id !== user.id));
      else if (result === "requested") setMyRequests((prev) => [...prev, user.id]);
      else if (result === "request_cancelled") setMyRequests((prev) => prev.filter((id) => id !== user.id));
      if (result) onFollowChange?.(user.id, result);
    } catch (error) {
      console.error("Error al seguir/dejar de seguir:", error);
    } finally {
      setBusyId(null);
    }
  };

  const followButton = (user) => {
    if (!myUid || user.id === myUid) return null;
    const isFollowing = myFollowing.includes(user.id);
    const isRequested = myRequests.includes(user.id);
    const filled = !isFollowing && !isRequested;
    return (
      <Button
        size="small"
        disableElevation
        variant={filled ? "contained" : "outlined"}
        disabled={busyId === user.id}
        onClick={(e) => {
          e.stopPropagation();
          handleFollow(user);
        }}
        sx={{
          flexShrink: 0,
          minWidth: 96,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          ...(filled
            ? { bgcolor: brandColors.accent, color: followTextColor, "&:hover": { bgcolor: brandColors.accent, filter: "brightness(1.08)" } }
            : {
                color: "text.primary",
                borderColor: "divider",
                // El hover de outlinedPrimary del tema pone texto blanco (pensado
                // para fondo azul): acá el fondo es tenue, el texto sigue oscuro/claro.
                "&:hover": { borderColor: "text.primary", bgcolor: "action.hover", color: "text.primary" },
              }),
          "&.Mui-disabled": filled ? { bgcolor: brandColors.accent, color: followTextColor, opacity: 0.6 } : { color: "text.secondary" },
        }}
      >
        {isFollowing ? <T>Siguiendo</T> : isRequested ? <T>Solicitado</T> : <T>Seguir</T>}
      </Button>
    );
  };

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setSearch("");
    }
  }, [open, initialTab]);

  // Se olvidan las listas al cambiar de perfil.
  useEffect(() => setLists({}), [uid]);

  useEffect(() => {
    if (!open || !uid || lists[tab]) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, SOURCES[tab], uid));
        const ids = snap.exists() ? Object.keys(snap.data()) : [];
        const users = await Promise.all(
          ids.map(async (id) => {
            const userSnap = await getDoc(doc(db, "users", id)).catch(() => null);
            return userSnap?.exists() ? { id, ...userSnap.data() } : null;
          })
        );
        if (!cancelled) {
          const found = users
            .filter(Boolean)
            .sort((a, b) => String(a.displayName || "").localeCompare(String(b.displayName || "")));
          setLists((prev) => ({ ...prev, [tab]: found }));
        }
      } catch (error) {
        console.error("Error cargando seguidores/seguidos:", error);
        if (!cancelled) setLists((prev) => ({ ...prev, [tab]: [] }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, uid, tab, lists]);

  const current = lists[tab];
  const visible = useMemo(() => {
    if (!current) return null;
    const term = normalize(search.trim());
    return term ? current.filter((u) => normalize(u.displayName).includes(term)) : current;
  }, [current, search]);

  const openProfile = (id) => {
    onClose();
    navigate(`/profile/${id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={!isDesktop}
      PaperProps={{ sx: { borderRadius: isDesktop ? 3 : 0, height: isDesktop ? 560 : "100%" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {tab === "following" ? <T>Siguiendo</T> : <T>Seguidores</T>}
        </Typography>
        <IconButton onClick={onClose} aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab value="following" label={<T>Siguiendo</T>} />
        <Tab value="followers" label={<T>Seguidores</T>} />
      </Tabs>

      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: <SearchClearAdornment value={search} onChange={(e) => setSearch(e.target.value)} />,
          }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {visible === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 5, px: 3 }}>
            {search.trim()
              ? <T>No hay resultados.</T>
              : tab === "following"
              ? <T>Todavía no sigue a nadie.</T>
              : <T>Todavía no tiene seguidores.</T>}
          </Typography>
        ) : (
          <List disablePadding>
            {visible.map((user) => (
              <ListItemButton key={user.id} onClick={() => openProfile(user.id)} sx={{ px: 2, gap: 1 }}>
                <ListItemAvatar>
                  <Avatar src={user.profileImageUrl} alt={user.displayName}>
                    {String(user.displayName || "?").charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.displayName || "Usuario"}
                  primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                  sx={{ minWidth: 0 }}
                />
                {followButton(user)}
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
};

export default FollowListDialog;
